/**
 * Advanced search: search text in chosen fields, and conditions on fields.
 *
 * Searchable targets are metadata fields (by id), timecode layers (by id),
 * and four built-ins: `_title`, `_filename`, `_asset_id` and `_subtitles` (the text of every subtitle track).
 */
import { z } from 'zod';
import type { FieldType, MetadataSchemaDefinition } from './domain.ts';
import type { StratumDTO } from './strata.ts';

export const SEARCH_BUILTINS = { _title: 'Title', _filename: 'File name', _asset_id: 'Asset ID', _subtitles: 'Subtitles' } as const;
export type TargetKind = FieldType | 'builtin' | 'stratum';

export const RULE_OPS = [
  'contains', 'not_contains', 'equals', 'starts_with', 'empty', 'not_empty',
  'has', 'is', 'is_not', 'gt', 'lt', 'between', 'before', 'after', 'on', 'is_true', 'is_false', 'exists', 'not_exists',
] as const;
export type RuleOp = (typeof RULE_OPS)[number];

export const RuleSchema = z.object({
  field: z.string().regex(/^(f_[a-z0-9_]+|s_[a-z0-9_]+|_title|_filename|_asset_id|_subtitles)$/),
  op: z.enum(RULE_OPS),
  value: z.union([z.string().max(500), z.number(), z.array(z.string().max(200)).max(50)]).optional(),
  value2: z.union([z.string().max(100), z.number()]).optional(),
});
export type Rule = z.infer<typeof RuleSchema>;

export const AdvancedQuerySchema = z.object({
  /** Where the text query is searched; empty means everywhere (the normal search). */
  in: z.array(z.string().regex(/^(f_[a-z0-9_]+|s_[a-z0-9_]+|_title|_filename|_asset_id|_subtitles)$/)).max(40).default([]),
  match: z.enum(['all', 'any']).default('all'),
  rules: z.array(RuleSchema).max(20).default([]),
});
export type AdvancedQuery = z.infer<typeof AdvancedQuerySchema>;

/** Operators that make sense for a kind of field, in menu order. */
export function opsFor(kind: TargetKind): RuleOp[] {
  switch (kind) {
    case 'text': case 'rich_text': case 'timecode': case 'builtin': return ['contains', 'not_contains', 'equals', 'starts_with', 'empty', 'not_empty'];
    case 'tags': return ['contains', 'has', 'empty', 'not_empty'];
    case 'vocabulary': return ['is', 'is_not', 'empty', 'not_empty'];
    case 'number': return ['equals', 'gt', 'lt', 'between', 'empty', 'not_empty'];
    case 'date': case 'datetime': return ['on', 'before', 'after', 'between', 'empty', 'not_empty'];
    case 'boolean': return ['is_true', 'is_false', 'empty'];
    case 'stratum': return ['contains', 'exists', 'not_exists'];
  }
}

export const OP_LABELS: Record<RuleOp, string> = {
  contains: 'contains', not_contains: 'does not contain', equals: 'is exactly', starts_with: 'starts with', empty: 'is empty', not_empty: 'is not empty',
  has: 'has the tag', is: 'is any of', is_not: 'is none of', gt: 'is more than', lt: 'is less than', between: 'is between',
  before: 'is before', after: 'is after', on: 'is on', is_true: 'is yes', is_false: 'is no', exists: 'has any segment', not_exists: 'has no segments',
};

/** Ops that take no value. */
export const VALUELESS_OPS: RuleOp[] = ['empty', 'not_empty', 'is_true', 'is_false', 'exists', 'not_exists'];

export interface SearchTarget { id: string; label: string; kind: TargetKind; options?: Array<{ value: string; label: string }> }

/** Everything a person can search in or put conditions on, for the given model and layers. */
export function searchTargets(def: MetadataSchemaDefinition | undefined, strata: StratumDTO[] = []): SearchTarget[] {
  return [
    { id: '_title', label: SEARCH_BUILTINS._title, kind: 'builtin' },
    { id: '_filename', label: SEARCH_BUILTINS._filename, kind: 'builtin' },
    { id: '_asset_id', label: SEARCH_BUILTINS._asset_id, kind: 'builtin' },
    { id: '_subtitles', label: SEARCH_BUILTINS._subtitles, kind: 'builtin' },
    ...(def?.fields ?? []).filter((f) => f.id !== 'f_title').map((f) => ({ id: f.id, label: f.label, kind: f.type as TargetKind, options: f.options })),
    ...strata.filter((s) => !s.archived && s.searchable).map((s) => ({ id: s.id, label: `${s.label} (timecoded)`, kind: 'stratum' as const, options: s.options })),
  ];
}

/** Split a query into terms; "quoted phrases" stay together. */
export function searchTerms(q: string): string[] {
  const out: string[] = [];
  const re = /"([^"]+)"|(\S+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(q))) out.push((m[1] ?? m[2]!).trim());
  return out.filter(Boolean).slice(0, 12);
}
